import { useEffect, useRef, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";

import type { ShellOutletContext } from "../nav/AppShell";
import { AdminSectionHead } from "../components/AdminSectionHead";
import { ChannelCreateForm, ChannelList } from "../surfaces/ChannelList";
import { ChannelThread } from "../surfaces/ChannelThread";
import { fetchClientSiteSetup, type ClientSiteSetup } from "../data/clients-sites";
import {
  channelUnreadIds,
  fetchUnreadChannels,
  markChannelReadCursor,
  type ChannelUnreadListLoad,
} from "../data/channel-unread";
import {
  CHANNELS_MANAGE_PERMISSION,
  COMPANY_CHANNEL_TARGET,
  channelTargetOptions,
  fetchChannelPosts,
  fetchChannels,
  type Channel,
  type ChannelPostsLoad,
  type ChannelsLoad,
} from "../data/channels";
import { useReadModel } from "../data/useReadModel";

// Channels (design draft 1 plate 07). Boards, not chat: company, per-site, and per-shift-group
// announcement boards that the same guards read on their phones.
//
// Its own page rather than an Admin section, for the same reason Feed is: a board list plus a
// composer is a working surface, not a settings row. Reads are ungated (console is management-only and
// RLS scopes the data); creating a channel and posting are behind channel.manage.

interface ChannelDetailLoad {
  readonly channelId: string;
  readonly load: ChannelPostsLoad;
}

export function ChannelsPage({ embedded = false }: { readonly embedded?: boolean }) {
  const { client, config, can } = useOutletContext<ShellOutletContext>();
  const [searchParams, setSearchParams] = useSearchParams();
  const canManage = can(CHANNELS_MANAGE_PERMISSION);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [postsNonce, setPostsNonce] = useState(0);
  const [unreadNonce, setUnreadNonce] = useState(0);
  const markAttemptRef = useRef<string | null>(null);
  const selectedChannelId = searchParams.get("channel");

  const channels = useReadModel<ChannelsLoad>(
    () => fetchChannels(client, config.accountId),
    [client, config.accountId, reloadNonce],
    { pollMs: config.refreshMs },
  );
  const unreadChannels = useReadModel<ChannelUnreadListLoad>(
    () => fetchUnreadChannels(client, config.accountId),
    [client, config.accountId, unreadNonce],
    { pollMs: config.refreshMs },
  );

  // The site list only fills the create form's target picker. If it fails, company-wide channels
  // still work rather than the whole page dying over an optional dropdown.
  const sites = useReadModel<ClientSiteSetup>(
    () => fetchClientSiteSetup(client, config.accountId),
    [client, config.accountId],
  );

  const available = channels.data !== null && channels.data.status === "ready" ? channels.data.channels : [];
  const selected = available.find((channel) => channel.channelId === selectedChannelId) ?? null;
  const selectedChannelIdForDetail = selected?.channelId ?? null;

  const posts = useReadModel<ChannelDetailLoad | null>(
    () =>
      selectedChannelIdForDetail === null
        ? Promise.resolve(null)
        : fetchChannelPosts(client, config.accountId, selectedChannelIdForDetail).then((load) => ({
            channelId: selectedChannelIdForDetail,
            load,
          })),
    [client, config.accountId, selectedChannelIdForDetail ?? "", postsNonce],
  );
  const selectedPosts =
    selectedChannelIdForDetail !== null && posts.data?.channelId === selectedChannelIdForDetail
      ? posts.data.load
      : null;
  const selectedPostsStatus =
    posts.status === "error" ? "error" : selectedPosts === null ? "loading" : "ready";
  const unreadChannelIds = channelUnreadIds(
    unreadChannels.status === "ready" ? unreadChannels.data : null,
  );

  useEffect(() => {
    if (
      selectedChannelIdForDetail === null ||
      selectedPosts?.status !== "ready" ||
      posts.status !== "ready"
    ) {
      return;
    }
    const newestRenderedMessageId = selectedPosts.posts[0]?.messageId ?? null;
    const attemptKey = `${config.accountId}:${selectedChannelIdForDetail}:${newestRenderedMessageId ?? "empty"}`;
    if (markAttemptRef.current === attemptKey) return;
    markAttemptRef.current = attemptKey;
    let active = true;

    void markChannelReadCursor(
      client,
      config.accountId,
      selectedChannelIdForDetail,
      newestRenderedMessageId,
    ).then((outcome) => {
      if (!outcome.ok) {
        if (markAttemptRef.current === attemptKey) markAttemptRef.current = null;
        return;
      }
      if (active) setUnreadNonce((nonce) => nonce + 1);
    });

    return () => {
      active = false;
    };
  }, [client, config.accountId, posts.status, selectedChannelIdForDetail, selectedPosts]);

  const targetOptions =
    sites.data === null ? [COMPANY_CHANNEL_TARGET] : channelTargetOptions(sites.data.sites, sites.data.clients);
  const targetNote =
    sites.status === "error"
      ? "Site list unavailable, so a new channel can only be company-wide right now."
      : null;

  return (
    <div className={embedded ? "channels-page inbox-host-page" : "page channels-page"}>
      {embedded ? (
        <h2 className="page__title">Boards</h2>
      ) : (
        <h1 className="page__title">Channels</h1>
      )}
      <p className="page__note">
        Announce only. No reply. Company, site, and shift group announcements stay on Boards.
        Direct and crew conversations stay in Messages. On first use, existing announcements
        remain unread until you open their board.
      </p>

      {canManage ? (
        <section className="admin__section" aria-label="New channel">
          <AdminSectionHead
            title="New channel"
            scope="account"
            hint="A board people can be announced to. Name it the way a guard would say it out loud."
          />
          <ChannelCreateForm
            client={client}
            accountId={config.accountId}
            targetOptions={targetOptions}
            targetNote={targetNote}
            onCreated={() => setReloadNonce((nonce) => nonce + 1)}
          />
        </section>
      ) : null}

      <section id="boards-publish" className="admin__section" aria-label={embedded ? "Boards" : "Channels"}>
        <AdminSectionHead
          title="Channels"
          scope="account"
          hint="Company first, then sites, then shift groups · the same order the phone shows."
        />
        <ChannelsBody
          load={channels.data}
          status={channels.status}
          error={channels.error}
          selectedChannelId={selectedChannelId}
          unreadChannelIds={unreadChannelIds}
          onSelect={(channel) => {
            const next = new URLSearchParams(searchParams);
            next.set("channel", channel.channelId);
            setSearchParams(next, { replace: true });
            setPostsNonce((nonce) => nonce + 1);
          }}
          timeZone={config.displayTimeZone}
        />
      </section>

      {selected !== null ? (
        <section className="admin__section" aria-label="Channel posts">
          <ChannelThread
            client={client}
            accountId={config.accountId}
            channel={selected}
            load={selectedPosts}
            status={selectedPostsStatus}
            error={posts.error}
            canManage={canManage}
            timeZone={config.displayTimeZone}
            onPosted={() => {
              setPostsNonce((nonce) => nonce + 1);
              setReloadNonce((nonce) => nonce + 1);
            }}
          />
        </section>
      ) : null}
    </div>
  );
}

function ChannelsBody({
  load,
  status,
  error,
  selectedChannelId,
  unreadChannelIds,
  onSelect,
  timeZone,
}: {
  readonly load: ChannelsLoad | null;
  readonly status: "loading" | "ready" | "error";
  readonly error: string | null;
  readonly selectedChannelId: string | null;
  readonly unreadChannelIds: ReadonlySet<string>;
  readonly onSelect: (channel: Channel) => void;
  readonly timeZone: string;
}) {
  if (status === "loading" && load === null) {
    return (
      <p className="surface__state" role="status">
        Loading channels...
      </p>
    );
  }
  if (status === "error") {
    return (
      <p className="surface__state surface__state--error" role="alert">
        {error ?? "Could not load channels."}
      </p>
    );
  }
  if (load === null) return null;

  // "Not deployed" is not "no channels": only one of them means nothing has been set up.
  if (load.status === "unavailable" || load.status === "failed") {
    return (
      <p
        className={load.status === "failed" ? "surface__state surface__state--error" : "surface__state"}
        role={load.status === "failed" ? "alert" : "status"}
      >
        {load.reason}
      </p>
    );
  }

  return (
    <ChannelList
      channels={load.channels}
      selectedChannelId={selectedChannelId}
      unreadChannelIds={unreadChannelIds}
      onSelect={onSelect}
      timeZone={timeZone}
    />
  );
}
